import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Inspection } from "@/types/Inspection.type";
import { Measurement } from "@/types/Measurement";
import { InspectionDefect } from "@/types/InspectionDefect";
import { PartialInspection } from "@/types/ModuleComponentProp.type";
import { Image } from "@/types/Image.type";
import { logout } from "./authSlice";

export type OpenInspectionLoadErrorReason = 'offline_lite' | 'fetch_failed';

export interface OpenInspectionState {
    inspection?: Inspection;
    isModified: boolean;
    /** Set when openInspectionById fails; cleared on successful open. Not persisted. */
    loadError: { inspectionId: string; reason: OpenInspectionLoadErrorReason } | null;
}
const initialState: OpenInspectionState = {
    inspection: undefined,
    isModified: false,
    loadError: null,
};

export interface ImageUrls {
    existing: Image[],
    new: Image[]
}

export interface CheckedModulePayload {
    moduleName: string;
    isChecked: boolean;
}

export interface GpsCoordinatesPayload {
    id: string;
    latitude: number;
    longitude: number;
}

const openInspectionSlice = createSlice({
    name: "openInspection",
    initialState,
    reducers: {
        openInspection: (state, action: PayloadAction<Inspection>) => {
            state.loadError = null;
            if (state.inspection?.id !== action.payload.id) {
                const inspection = { ...action.payload };
                if (!inspection.startTime) {
                    inspection.startTime = new Date().toISOString();
                }
                state.inspection = inspection;
            }
        },
        setOpenInspectionLoadError: (
            state,
            action: PayloadAction<{ inspectionId: string; reason: OpenInspectionLoadErrorReason }>,
        ) => {
            state.loadError = action.payload;
        },
        clearOpenInspectionLoadError: (state) => {
            state.loadError = null;
        },
        updateOpenedInspection: (state, action: PayloadAction<PartialInspection>) => {
            if (state.inspection) {
                // Update the found inspection object with new values from the payload
                Object.assign(state.inspection, action.payload);
                state.isModified = true;
                console.log('[openInspectionSlice updateOpenedInspection] opened inspection updated');

            } else {
                console.error("No inspection opened for update");
                // state.openedInspection = action.payload;
            }
        },
        /** Updates only GPS coordinates; does not set isModified (background capture). */
        setOpenedInspectionGps: (state, action: PayloadAction<GpsCoordinatesPayload>) => {
            const { id, latitude, longitude } = action.payload;
            if (state.inspection?.id === id) {
                state.inspection.latitude = latitude;
                state.inspection.longitude = longitude;
            }
        },
        addDefect: (state, action: PayloadAction<InspectionDefect>) => {
            if (state.inspection) {
                const createdOn = new Date().toISOString();
                const newDefect: InspectionDefect = {
                    ...action.payload,
                    isNew: true,
                    createdOn
                };
                state.inspection.qcDefects.push(newDefect);
                state.isModified = true;
                console.log('Inspection defects updated');
            } else {
                alert('Inspection not found in draft for edit');
            }
        },
        storeMeasurements: (state, action: PayloadAction<Measurement[]>) => {
            if (state.inspection) {
                state.inspection.measurements = action.payload;
                state.isModified = true;
                console.log('Measurements stored successfully!');
            } else {
                alert('Inspection not found in draft for edit');
            }
        },
        deleteDefect: (state, action: PayloadAction<string>) => {
            const defectId = action.payload;

            if (state.inspection) {
                // Create a new array for qcDefects without the defect being deleted
                state.inspection.qcDefects = state.inspection.qcDefects.filter(d => d.id !== defectId);
                state.isModified = true;
                console.log('inspection defect deleted');
            }

        },
        editDefect: (state, action: PayloadAction<InspectionDefect>) => {
            const defect = action.payload;
            if (state.inspection) {
                const key = (d: InspectionDefect) => d.id ?? d.newId;
                const existingDefectIndex = state.inspection.qcDefects.findIndex(d => key(d) === key(defect));
                if (existingDefectIndex !== -1) {
                    state.inspection.qcDefects[existingDefectIndex] = defect;
                    state.isModified = true;

                } else {
                    console.error('No defect found for updating', JSON.stringify(action.payload));

                }
            } else {
                console.error('No inspection found for updating defect', JSON.stringify(action.payload));
            }
        },

        closeInspection: (state, action: PayloadAction<string>) => {
            if (state.inspection?.id === action.payload) {
                state.inspection = undefined;
                state.isModified = false;
                state.loadError = null;
                console.log('inspection closed');

            } else {
                console.log("Inspection not found for closing");
            }
        },
        submit: (state, action: PayloadAction<Inspection>) => {
            if (state.inspection?.id === action.payload.id) {
                Object.assign(state.inspection, action.payload);
                state.inspection = undefined;
                state.isModified = false;
            } else {
                console.log("Inspection not found for marking save");
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(logout, () => initialState); // Reset state on logout
    }
});

export const {
    openInspection,
    updateOpenedInspection,
    setOpenedInspectionGps,
    closeInspection,
    deleteDefect,
    addDefect,
    editDefect,
    storeMeasurements,
    submit,
    setOpenInspectionLoadError,
    clearOpenInspectionLoadError,
} = openInspectionSlice.actions;
export default openInspectionSlice.reducer;
